import { Box, Button, Flex, useToast } from '@chakra-ui/react';
import { useCallback, useRef, useState } from 'react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { useAuthTenant } from '@duke-hq/app-provider-web/tenant/useAuthTenant';
import { trpcTenant } from '@duke-hq/app-provider-web/tenant/trpcTenant';
import {
  FormContentContainer,
  FormFooterContainer,
  FormMainContainer,
} from '@duke-hq/ui/components/Modals/FormModalLayouts';
import {
  TeamIndividualUserForm,
  TeamIndividualUserFormRef,
  type TeamIndividualUserFormData,
} from '@duke-hq/app-provider-web/tenant/Team/TeamBulkUsers/TeamIndividualUserForm';
import { ConsultantRoleSchema } from '@duke-hq/svc-api/provider/util/consultants';

export function TeamIndividualUser({
  member,
  allowRoleSelection = true,
  onClose,
}: {
  member?: TeamIndividualUserFormData;
  allowRoleSelection?: boolean;
  onClose?: () => void;
}): React.ReactElement {
  const formRef = useRef<TeamIndividualUserFormRef>(null);
  const toast = useToast();
  const utils = trpcTenant.useUtils();

  const [isFormValidating, setIsFormValidating] = useState(false);
  const [isFormSubmittingInternally, setIsFormSubmittingInternally] = useState(false);

  const auth = useAuthTenant();

  const createMember = trpcTenant.users.createConsultant.useMutation({
    onSuccess() {
      toast({
        title: t`Team member created successfully.`,
        status: 'success',
        duration: 7000,
        isClosable: true,
      });
    },
    onError: () => {
      toast({
        title: t`Failed to create team member.`,
        status: 'error',
        duration: 7000,
        isClosable: true,
      });
    },
    onSettled: () => {
      void utils.users.listMembersWithPagination.refetch();
      if (onClose) {
        onClose();
      }
    },
  });

  const handleFormStateChange = useCallback(
    (submitting: boolean, loading: boolean) => {
      setIsFormSubmittingInternally(submitting);
      setIsFormValidating(loading);
    },
    [setIsFormSubmittingInternally, setIsFormValidating],
  );

  const handleExternalSubmit = useCallback(async () => {
    if (formRef.current) {
      const formData = await formRef.current.getValidatedData();

      if (formData) {
        await createMember.mutateAsync({
          ...formData,
          email: formData.email,
          providerId: auth?.user.tenant.id,
          role:
            ConsultantRoleSchema.safeParse(formData.role).data ??
            ConsultantRoleSchema.enum.CONSULTANT,
          region: formData?.region ?? undefined,
          site: formData?.site ?? undefined,
        });
      }
    }
  }, [formRef, createMember, auth?.user.tenant.id]);

  const isApiPending = createMember.isPending;
  const isButtonDisabled =
    !!member?.deletedAt || isFormValidating || isFormSubmittingInternally || isApiPending;

  return (
    <Box
      as="form"
      onSubmit={(e: React.FormEvent<HTMLFormElement>) => e.preventDefault()}
      style={{ width: '100%' }}
      noValidate
    >
      <FormMainContainer>
        <FormContentContainer>
          <TeamIndividualUserForm
            ref={formRef}
            member={member}
            allowRoleSelection={allowRoleSelection}
            onClose={onClose}
            onFormStateChange={handleFormStateChange}
          />
        </FormContentContainer>

        <FormFooterContainer>
          <Flex justify={'flex-end'} gap={2}>
            <Button type="button" variant="ghost" onClick={onClose}>
              <Trans>Cancel</Trans>
            </Button>
            <Button
              type="submit"
              onClick={handleExternalSubmit}
              isLoading={isButtonDisabled}
              isDisabled={isButtonDisabled}
            >
              <Trans>Add team member</Trans>
            </Button>
          </Flex>
        </FormFooterContainer>
      </FormMainContainer>
    </Box>
  );
}
